import { normalize, schema } from 'normalizr'
import {
  IBankAccountObj,
  ICreateBankAccountObj,
  ILegalEntityObj,
  IMap,
  IOrgCreateObj,
  IOrgObj,
  IServiceObject,
  IStateUserRole,
  IPromotion
} from '../types'
import { serverApi } from '../utils/request'
import { IRemovedOrganizationBankAccountResponseDTO } from '../types/organization'

const Roles: IStateUserRole[] = [
  {
    id: '1',
    type: '',
    roleName: 'Super Admin'
  },
  {
    id: '2',
    type: '',
    roleName: 'Sponsor'
  },
  {
    id: '3',
    type: '',
    roleName: 'Provider'
  },
  {
    id: '4',
    type: '',
    roleName: 'Broker'
  }
]

class OrganizationsService {
  private urlBase = '/api/v1/organizations'

  private entityBankAccount = new schema.Entity(
    'bankAccounts',
    {},
    {
      processStrategy: (entity, parent) => ({
        id: entity.id,
        bankName: entity.bank_name,
        isPrimary: entity.default_for_currency,
        lastFourNumbers: entity.last4,
        orgId: parent.id
      })
    }
  )
  private entiryPromotion = new schema.Entity('promotion')
  private entityOrganization = new schema.Entity('organizations', {
    bankAccounts: [this.entityBankAccount]
  })
  private entityProduct = new schema.Entity('products')
  private entityService = new schema.Entity('services', {
    providerProducts: [this.entityProduct]
  })
  private schemaOrganization = [this.entityOrganization]

  public async getAllOrganizations(): Promise<IOrgObj[]> {
    const organizations = await serverApi.get<IOrgObj[]>(`${this.urlBase}/doGetByUserId`)
    return organizations.data
  }

  public async getOrganizationsByDriverId(
    driverId: string
  ): Promise<{ organizations: IMap<IOrgObj>; services: IMap<IServiceObject> }> {
    const result = await serverApi.get(`${this.urlBase}/doGetByOperatorId/${driverId}`)
    const entityOrganization = new schema.Entity('organizations', {
      services: [this.entityService]
    })
    const { entities } = normalize(result.data, [entityOrganization])
    return {
      organizations: entities.organizations,
      services: entities.services
    }
  }

  public search(query: string): Promise<{ [key: string]: IOrgObj }> {
    return serverApi.get(`${this.urlBase}/doSearch/${query}`).then(
      result => {
        const data = normalize(result.data, this.schemaOrganization)
        return data.entities ? data.entities.organizations : {}
      },
      error => {
        throw error.data
      }
    )
  }

  public getAllRoles(): Promise<IStateUserRole[]> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(Roles)
      }, 1000)
    })
  }

  public async createOrg(data: IOrgCreateObj): Promise<IMap<IOrgObj>> {
    const createdOrg = await serverApi.post<IOrgObj>(`${this.urlBase}/create`, data)
    return {
      [createdOrg.data.id]: createdOrg.data
    }
  }

  public async createOrganizationBankAccount(
    data: ICreateBankAccountObj,
    orgId: string
  ): Promise<IMap<IBankAccountObj>> {
    const createdOrganizationBankAccount = await serverApi.post<IBankAccountObj>(
      `${this.urlBase}/doCreateBankAccount`,
      {
        ...data,
        orgId
      }
    )
    return {
      [createdOrganizationBankAccount.data.id]: createdOrganizationBankAccount.data
    }
  }

  public createOrganizationLegalEntity(data: ILegalEntityObj, orgId: string): Promise<any> {
    return serverApi
      .post(`${this.urlBase}/doCreateLegalEntity`, {
        ...data,
        orgId
      })
      .then(result => result.data)
  }

  public getOrganizationLegalEntity(orgId: string): Promise<any> {
    return serverApi.get(`${this.urlBase}/legalEntity/${orgId}`).then(result => {
      const info: any = result.data as any
      return { ...info.accountInfo, ...info.address, bankAccounts: info.bankAccounts }
    })
  }

  public async updateOrg(data: IOrgObj): Promise<IOrgObj> {
    const updateOrg = await serverApi.put<IOrgObj>(`${this.urlBase}`, data)
    return updateOrg.data
  }

  public async updateOrgLegalEntity(organizationLegalEntity: any, orgId: string): Promise<any> {
    const updateOrgLegalEntity = await serverApi.post(`${this.urlBase}/updateAccount`, {
      orgId,
      address: {
        //country: organizationLegalEntity.country,
        country: 'USA',
        city: organizationLegalEntity.city,
        state: organizationLegalEntity.state,
        addressLine1: organizationLegalEntity.addressLine1,
        addressLine2: organizationLegalEntity.addressLine2,
        postalCode: organizationLegalEntity.postalCode
      },
      accountInfo: {
        firstName: organizationLegalEntity.firstName,
        lastName: organizationLegalEntity.lastName,
        businessName: organizationLegalEntity.businessName,
        businessTaxId: organizationLegalEntity.businessTaxId,
        type: organizationLegalEntity.type
      }
    })
    return updateOrgLegalEntity
  }

  public async getSingleOrg(id: string): Promise<IOrgObj> {
    const organization = await serverApi.get<IOrgObj>(`${this.urlBase}/${id}`).catch(error => {
      throw error.data
    })
    return organization.data
  }

  public async removeOrganizationBankAccount(orgId: string, bankAccountId: string): Promise<string> {
    const result = await serverApi.delete<IRemovedOrganizationBankAccountResponseDTO>(
      `${this.urlBase}/doRemoveBankAccount`,
      {
        params: {
          bankAccountId,
          orgId
        }
      }
    )
    return result.data.id
  }

  public setOrganizationDefaultBankAccount(orgId: string, bankAccountId: string): Promise<any> {
    return serverApi
      .patch(`${this.urlBase}/doSetDefaultBankAccount`, {
        bankAccountId,
        orgId
      })
      .then(result => {
        const { entities } = normalize(result.data, this.entityOrganization)
        if (!entities.bankAccounts) {
          return {}
        }
        return entities.bankAccounts
      })
  }
}

const organizationsService = new OrganizationsService()
export default organizationsService



// WEBPACK FOOTER //
// ./src/services/Organizations.ts